import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import Swal from 'sweetalert2';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import '../UserPermissions.css';

interface Permission {
  id: string;
  name: string;
  active: boolean;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  user_permissions: Permission[];
}

interface EditPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSave: (user: User) => void;
}

const EditPermissionsModal: React.FC<EditPermissionsModalProps> = ({
  isOpen,
  onClose,
  user,
  onSave
}) => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const originalPermissionsRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (user?.user_permissions) {
      const permissionValues: Record<string, boolean> = {};

      user.user_permissions.forEach(permission => {
        permissionValues[permission.id] = permission.active;
      });

      originalPermissionsRef.current = { ...permissionValues };
      setPermissions(permissionValues);
    }
  }, [user]);

  const handlePermissionToggle = (permissionId: string): void => {
    setPermissions(prev => ({
      ...prev,
      [permissionId]: !prev[permissionId]
    }));
  };

  const hasChanges = (): boolean => {
    const originalPermissions = originalPermissionsRef.current;
    return Object.keys(permissions).some(id =>
      permissions[id] !== originalPermissions[id]
    );
  };

  const handleSave = (): void => {
    if (!user || !hasChanges()) return;


    const originalPermissions = originalPermissionsRef.current;
    const changedPermissions: { id: string; active: boolean }[] = [];

    Object.keys(permissions).forEach(id => {
      const hasChanged = permissions[id] !== originalPermissions[id];

      if (hasChanged) {
        changedPermissions.push({
          id,
          active: permissions[id]
        });
      }
    });

    if (changedPermissions.length === 0) {
      return;
    }
    setIsSaving(true);

    const payload = {
      user_id: user.id,
      permissions: changedPermissions
    };

    axios.patch(`${environment.baseUrl}/permissions/`, payload)
      .then(() => {
        const updatedPermissions = user.user_permissions.map(permission => ({
          ...permission,
          active: permissions[permission.id]
        }));

        const updatedUser: User = { ...user, user_permissions: updatedPermissions };

        onSave(updatedUser);

        Swal.fire({
          title: `${changedPermissions.length} permission${changedPermissions.length > 1 ? 's' : ''} updated successfully`,
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        onClose();
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error updating permissions', error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleClose = (): void => {
    setPermissions({});
    originalPermissionsRef.current = {};
    onClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader className="h3">
        Edit Permissions for {user ? `${user.first_name} ${user.last_name}` : ''}
      </ModalHeader>
      <ModalBody>
        {user && (
          <Form>
            {user.user_permissions.map((permission) => (
              <FormGroup key={permission.id} className="d-flex align-items-center justify-content-between pb-2 mb-2">
                <Label className="mb-0">
                  {permission.name}
                </Label>
                <div>
                  <Input
                    type="checkbox"
                    checked={permissions[permission.id] || false}
                    onChange={() => handlePermissionToggle(permission.id)}
                    className="form-check-input-lg"
                    disabled={isSaving}
                  />
                </div>
              </FormGroup>
            ))}
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          size="sm"
          onClick={handleSave}
          disabled={!hasChanges() || isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />
              <output>Saving...</output>
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
        <Button
          color="secondary"
          size="sm"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditPermissionsModal;